// src/pages/LearnerDashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import '../styles/learnerDashboard.css';

const safeTimestampToDate = (timestamp) => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp === undefined || timestamp === null) return null;
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

function getAssignmentDates(assignment, userGroupId) {
  let startDate = null;
  let endDate = null;

  if (assignment.groupSettings?.[userGroupId]) {
    const gs = assignment.groupSettings[userGroupId];
    if (gs.startDate) startDate = safeTimestampToDate(gs.startDate);
    if (gs.endDate) endDate = safeTimestampToDate(gs.endDate);
  }

  if (!startDate && assignment.startDate) startDate = safeTimestampToDate(assignment.startDate);
  if (!endDate && assignment.endDate) endDate = safeTimestampToDate(assignment.endDate);

  if (!startDate) startDate = new Date(Date.now() - 86400000);
  if (!endDate) endDate = new Date(Date.now() + 604800000);

  return { startDate, endDate };
}

function LearnerDashboard() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'current');
  const { currentUser } = useAuth();

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userGroupId, setUserGroupId] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [mentorName, setMentorName] = useState('');
  const [submissionStats, setSubmissionStats] = useState({ total: 0, submitted: 0, reviewed: 0 });
  const [userName, setUserName] = useState('Learner');

  useEffect(() => {
    if (location.state?.activeTab === 'history') {
      setActiveTab('portfolio');
    }
  }, [location.state?.activeTab]);

  const now = useMemo(() => new Date(), []);

  const processAssignments = useCallback((assignmentsData, submissionsData) => {
    return assignmentsData.map(assignment => {
      const submission = submissionsData.find(s => s.assignmentId === assignment.id);

      let submissionStatus = 'Not Started';
      if (submission) {
        if (submission.submissionStatus === 'draft') {
          submissionStatus = 'In Progress';
        } else if (submission.submissionStatus === 'submitted') {
          submissionStatus = submission.readyForReview ? 'Submitted' : 'In Progress';
        } else if (submission.submissionStatus === 'updated') {
          submissionStatus = 'Updated';
        }
        if (submission.reviewStatus === 'reviewed') submissionStatus = 'Reviewed';
      }

      const { startDate, endDate } = getAssignmentDates(assignment, userGroupId);
      const nowTime = now.getTime();

      let tabCategory;
      if (nowTime < startDate.getTime()) {
        tabCategory = 'upcoming';
      } else if (nowTime > endDate.getTime()) {
        tabCategory = 'history';
      } else {
        tabCategory = 'current';
      }

      return {
        ...assignment,
        submission,
        submissionStatus,
        inPortfolio: submission?.reviewStatus === 'reviewed',
        tabCategory,
        startDate,
        endDate
      };
    });
  }, [now, userGroupId]);

  useEffect(() => {
    const fetchUserGroup = async () => {
      if (!currentUser) return;
      try {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (!userDoc.exists()) return;

        const userData = userDoc.data();
        setUserName(userData.name || 'Learner');

        const groupId = userData.groupId || null;
        setUserGroupId(groupId);
        if (!groupId) return;

        const groupDoc = await getDoc(doc(db, "groups", groupId));
        if (!groupDoc.exists()) return;

        const groupData = groupDoc.data();
        setGroupName(groupData.name || 'Unnamed Group');

        if (groupData.mentorId) {
          const mentorDoc = await getDoc(doc(db, "users", groupData.mentorId));
          if (mentorDoc.exists()) {
            setMentorName(mentorDoc.data().name || 'Unnamed Mentor');
          }
        }
      } catch (error) {
        console.error("Error fetching user group:", error);
      }
    };
    fetchUserGroup();
  }, [currentUser]);

  useEffect(() => {
    const loadAssignmentsData = async () => {
      if (!userGroupId || !currentUser) {
        if (currentUser) setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const db = getFirestore();

        const assignmentSnapshot = await getDocs(
          query(collection(db, "assignments"), orderBy("order", "asc"))
        );

        const assignmentsData = [];
        assignmentSnapshot.forEach(docSnap => {
          const data = docSnap.data();
          const targetGroups = data.targetGroups || [];
          if (targetGroups.length > 0 && !targetGroups.includes(userGroupId)) return;

          const gs = data.groupSettings?.[userGroupId];
          const toDate = (val) => val && typeof val.toDate === 'function' ? val.toDate() : null;
          const startDate = toDate(gs?.startDate) || toDate(data.startDate) || new Date(Date.now() - 86400000);
          const endDate = toDate(gs?.endDate) || toDate(data.endDate) || new Date(Date.now() + 604800000);

          assignmentsData.push({
            id: docSnap.id,
            title: data.title || 'Untitled Assignment',
            instruction: data.instruction || '',
            startDate,
            endDate,
            order: data.order || 0,
            status: data.status || 'unknown',
            targetGroups,
            groupSettings: data.groupSettings || {}
          });
        });

        const submissionSnapshot = await getDocs(
          query(collection(db, "submissions"), where("learnerId", "==", currentUser.uid))
        );

        const submissionsData = submissionSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const processed = processAssignments(assignmentsData, submissionsData);
        setAssignments(processed);

        const uniqueSubmitted = new Set(
          submissionsData
            .filter(s => s.submissionStatus === 'submitted' || s.submissionStatus === 'updated')
            .map(s => s.assignmentId)
        );
        const uniqueReviewed = new Set(
          submissionsData.filter(s => s.reviewStatus === 'reviewed').map(s => s.assignmentId)
        );

        setSubmissionStats({
          total: assignmentsData.length,
          submitted: uniqueSubmitted.size,
          reviewed: uniqueReviewed.size
        });
      } catch (error) {
        console.error("Error loading assignments:", error);
      } finally {
        setLoading(false);
      }
    };
    loadAssignmentsData();
  }, [userGroupId, currentUser, processAssignments]);

  const assignmentLists = useMemo(() => {
    const sortByOrder = (a, b) => a.order - b.order;
    return {
      current: assignments.filter(a => a.tabCategory === 'current').sort(sortByOrder),
      upcoming: assignments.filter(a => a.tabCategory === 'upcoming').sort(sortByOrder),
      portfolio: assignments.filter(a => a.tabCategory === 'history').sort((a, b) => b.endDate - a.endDate)
    };
  }, [assignments]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'current':
        return (
          <div className="dashboard-section">
            <h2>Current Assignments</h2>
            {assignmentLists.current.length > 0 ? (
              <div className="assignment-list">
                {assignmentLists.current.map(assignment => (
                  <Link to={`/assignments/${assignment.id}`} key={assignment.id} className="assignment-item">
                    <div className="assignment-details">
                      <h3>{assignment.title}</h3>
                      <p>Start: {formatDate(assignment.startDate, 'short')}</p>
                      <p>Due: {formatDate(assignment.endDate, 'short')}</p>
                      <p className="assignment-preview">
                        {assignment.instruction && assignment.instruction.length > 100
                          ? `${assignment.instruction.substring(0, 100)}...`
                          : assignment.instruction || "No instructions provided."}
                      </p>
                    </div>
                    <div className="assignment-status">{assignment.submissionStatus}</div>
                  </Link>
                ))}
              </div>
            ) : (
              <p>No current assignments.</p>
            )}
          </div>
        );

      case 'upcoming':
        return (
          <div className="dashboard-section">
            <h2>Upcoming Assignments</h2>
            {assignmentLists.upcoming.length > 0 ? (
              <div className="assignment-list">
                {assignmentLists.upcoming.map(assignment => (
                  <div key={assignment.id} className="assignment-item">
                    <div className="assignment-details">
                      <h3>{assignment.title}</h3>
                      <p>Start: {formatDate(assignment.startDate, 'short')}</p>
                      <p>Due: {formatDate(assignment.endDate, 'short')}</p>
                    </div>
                    <div className="assignment-status">Upcoming</div>
                  </div>
                ))}
              </div>
            ) : (
              <p>No upcoming assignments.</p>
            )}
          </div>
        );

      case 'portfolio':
        return (
          <div className="dashboard-section">
            <h2>Portfolio</h2>
            <p>View all your completed assignments and submissions.</p>
            {assignmentLists.portfolio.length > 0 ? (
              <div className="assignment-list">
                {assignmentLists.portfolio.map(assignment => {
                  const wasSubmitted = assignment.submissionStatus !== 'Not Started' && assignment.submission;
                  const statusText = wasSubmitted ? assignment.submissionStatus : "Not Submitted";
                  return (
                    <Link
                      to={`/portfolio/${assignment.id}`}
                      key={assignment.id}
                      className={`assignment-item ${!wasSubmitted ? 'not-submitted' : ''}`}
                    >
                      <div className="assignment-details">
                        <h3>{assignment.title}</h3>
                        <p>Started: {formatDate(assignment.startDate, 'short')}</p>
                        <p>Ended: {formatDate(assignment.endDate, 'short')}</p>
                      </div>
                      <div className="assignment-status">{statusText}</div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="empty-portfolio">
                <p>Your portfolio is empty. Completed assignments will appear here.</p>
                <p className="helper-text">As you complete assignments, they will be automatically added to your portfolio.</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <div className="dashboard"><p>Loading your dashboard...</p></div>;
  }

  return (
    <div className="dashboard">
      <div className="tabbed-dashboard">
        <div className="dashboard-header">
          <h2>{greeting}, {userName}!</h2>
          <div className="info-item">
            <span className="info-label">Group:</span>
            <span className="info-value">{groupName || 'Not assigned to a group'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Mentor:</span>
            <span className="info-value">{mentorName || 'No mentor assigned'}</span>
          </div>
          <div className="stats-row">
            <div className="stat-box">
              <span className="stat-value">{submissionStats.total}</span>
              <span className="stat-label">Total Assignments</span>
            </div>
            <div className="stat-box">
              <span className="stat-value">{submissionStats.submitted}</span>
              <span className="stat-label">Submitted</span>
            </div>
            <div className="stat-box">
              <span className="stat-value">{submissionStats.reviewed}</span>
              <span className="stat-label">Reviewed</span>
            </div>
          </div>
        </div>

        <div className="tab-content">
          {renderTabContent()}
        </div>

        <nav className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            <span className="tab-icon">📝</span>
            <span className="tab-label">Current</span>
            {assignmentLists.current.length > 0 &&
              <span className="tab-badge">{assignmentLists.current.length}</span>}
          </button>

          <button
            className={`tab-button ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            <span className="tab-icon">📅</span>
            <span className="tab-label">Upcoming</span>
            {assignmentLists.upcoming.length > 0 &&
              <span className="tab-badge">{assignmentLists.upcoming.length}</span>}
          </button>

          <button
            className={`tab-button ${activeTab === 'portfolio' ? 'active' : ''}`}
            onClick={() => setActiveTab('portfolio')}
          >
            <span className="tab-icon">📚</span>
            <span className="tab-label">Portfolio</span>
            {assignmentLists.portfolio.length > 0 &&
              <span className="tab-badge">{assignmentLists.portfolio.length}</span>}
          </button>
        </nav>
      </div>
    </div>
  );
}

export default LearnerDashboard;